import { jsPDF } from "jspdf";
import autoTable, { type CellDef } from "jspdf-autotable";
import JSZip from "jszip";
import {
  buildCustomerPDFDoc,
  buildInvoicePDFDoc,
  buildPdfFilename,
  buildInvoiceFilename,
  type CompanySettings,
  type EstimateData,
  type InvoiceData,
} from "./pdf";
import {
  getEstimate,
  getInvoice,
  listEstimateMilestones,
  listMilestones,
  listInvoicePayments,
  type ProjectDetail,
  type ProjectAggregates,
  type ProjectQuote,
  type ProjectInvoice,
  type ProjectActualCost,
  type ProjectFile,
} from "@workspace/api-client-react";

export interface ProjectSummaryInput {
  project: ProjectDetail;
  agg: ProjectAggregates | null | undefined;
  quotes: ProjectQuote[];
  invoices: ProjectInvoice[];
  costs: ProjectActualCost[];
  files: ProjectFile[];
}

function hexToRgb(hex: string): [number, number, number] {
  const m = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return m
    ? [parseInt(m[1], 16), parseInt(m[2], 16), parseInt(m[3], 16)]
    : [30, 58, 95];
}

function fmtMoneyFn(currencyLabel: string) {
  const label = currencyLabel?.trim() || "USD";
  return (v: number) => {
    try {
      return new Intl.NumberFormat("en-US", { style: "currency", currency: label }).format(v);
    } catch {
      return `$${v.toFixed(2)}`;
    }
  };
}

function fmtDateStr(s: string | null | undefined): string {
  if (!s) return "—";
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [y, m, d] = s.split("-").map(Number);
      return new Date(y, m - 1, d).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
    }
    return new Date(s).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" });
  } catch {
    return s;
  }
}

const STATUS_LABELS: Record<string, string> = {
  lead: "Lead",
  quoted: "Quoted",
  approved: "Approved",
  in_progress: "In Progress",
  complete: "Complete",
  archived: "Archived",
};

const FOOTER_RESERVE = 28;

export function buildProjectSummaryPDFDoc(
  input: ProjectSummaryInput,
  company: CompanySettings = {},
): jsPDF {
  const { project, agg, quotes, invoices } = input;
  // Quote labour replaced by the crew's time log isn't an actual cost.
  const costs = input.costs.filter((c) => !c.replacedByTimeLog);
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "letter" });
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 18;
  const rightX = pageWidth - margin;

  const accentRgb = hexToRgb(company.accentColor || "#1e3a5f");
  const currencyLabel = company.currencyLabel || "USD";
  const fmt = fmtMoneyFn(currencyLabel);

  let y = margin;

  // Header: logo (left) + company info (right)
  const maxLogoW = 50;
  const maxLogoH = 28;
  let logoH = 0;
  if (company.logoBase64) {
    try {
      const props = doc.getImageProperties(company.logoBase64);
      const scale = Math.min(maxLogoW / props.width, maxLogoH / props.height);
      const lw = props.width * scale;
      const lh = props.height * scale;
      doc.addImage(company.logoBase64, "PNG", margin, y, lw, lh);
      logoH = lh;
    } catch {
      /* ignore */
    }
  }

  doc.setFont("helvetica", "normal");
  doc.setFontSize(28);
  doc.setTextColor(30, 30, 30);
  doc.text("Project Summary", rightX, y + 9, { align: "right" });

  const companyInfoY = y + 15;
  const companyLines: string[] = [];
  if (company.companyName) companyLines.push(company.companyName);
  if (company.email) companyLines.push(company.email);
  if (company.phone) companyLines.push(company.phone);
  doc.setFont("helvetica", "normal");
  doc.setFontSize(8.5);
  doc.setTextColor(80, 80, 80);
  companyLines.forEach((line, i) => {
    if (i === 0) {
      doc.setFont("helvetica", "bold");
      doc.setFontSize(10);
    } else {
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8.5);
    }
    doc.text(line, rightX, companyInfoY + i * 4.6, { align: "right" });
  });

  y = Math.max(y + logoH + 6, companyInfoY + companyLines.length * 4.6 + 6);

  doc.setDrawColor(accentRgb[0], accentRgb[1], accentRgb[2]);
  doc.setLineWidth(0.6);
  doc.line(margin, y, rightX, y);
  y += 8;

  // Project header block
  doc.setFont("helvetica", "bold");
  doc.setFontSize(18);
  doc.setTextColor(20, 20, 20);
  doc.text(project.name, margin, y);
  y += 8;

  const customer = project.customer;
  const headerRows: Array<[string, string]> = [];
  if (customer) headerRows.push(["Customer", customer.name]);
  if (customer?.email) headerRows.push(["Email", customer.email]);
  if (customer?.phone) headerRows.push(["Phone", customer.phone]);
  if (customer?.address) headerRows.push(["Address", customer.address]);
  headerRows.push(["Status", STATUS_LABELS[project.status] ?? project.status]);
  if (project.startDate) headerRows.push(["Start Date", fmtDateStr(project.startDate)]);
  if (project.targetCompletionDate) headerRows.push(["Target Completion", fmtDateStr(project.targetCompletionDate)]);
  headerRows.push(["Created", fmtDateStr(project.createdAt)]);

  doc.setFont("helvetica", "normal");
  doc.setFontSize(9.5);
  doc.setTextColor(60, 60, 60);
  for (const [label, value] of headerRows) {
    doc.setFont("helvetica", "bold");
    doc.text(`${label}:`, margin, y);
    doc.setFont("helvetica", "normal");
    doc.text(value, margin + 36, y);
    y += 5.2;
  }
  y += 3;

  // Financial Overview
  if (agg) {
    if (y + 50 > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
    sectionHeader(doc, "Financial Overview", y, margin, rightX, accentRgb);
    y += 8;

    const finRows: string[][] = [
      ["", "Quoted", "Actual", "Variance"],
      [
        "Total",
        fmt(agg.quotedTotal),
        fmt(agg.actualTotal),
        fmt(agg.variance),
      ],
    ];
    autoTable(doc, {
      startY: y,
      head: [finRows[0]],
      body: finRows.slice(1),
      theme: "plain",
      styles: { fontSize: 9.5, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 } },
      headStyles: { fillColor: accentRgb, textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
      columnStyles: {
        0: { cellWidth: 40, fontStyle: "bold" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
      },
    });
    y = (doc as any).lastAutoTable.finalY + 4;

    const pct = (num: number, denom: number) =>
      denom > 0 ? `${((num / denom) * 100).toFixed(1)}%` : "—";
    const summaryRows: string[][] = [
      ["Invoiced Total", `${fmt(agg.invoicedTotal)}  (${pct(agg.invoicedTotal, agg.quotedTotal)} of quoted)`],
      ["Paid", `${fmt(agg.paidTotal)}  (${pct(agg.paidTotal, agg.invoicedTotal)} of invoiced)`],
      ["Outstanding", fmt(agg.outstanding)],
      ["Projected Profit", fmt(agg.projectedProfit)],
      ["Actual Profit", fmt(agg.actualProfit)],
      ["Margin", `${(agg.margin || 0).toFixed(1)}%`],
    ];
    autoTable(doc, {
      startY: y,
      body: summaryRows,
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 } },
      margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
      columnStyles: {
        0: { cellWidth: 60, fontStyle: "bold", textColor: [60, 60, 60] },
        1: { halign: "right" },
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Quotes
  if (quotes.length > 0) {
    if (y + 30 > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
    sectionHeader(doc, `Quotes (${quotes.length})`, y, margin, rightX, accentRgb);
    y += 8;
    autoTable(doc, {
      startY: y,
      head: [["#", "Title", "Status", "Date", "Total"]],
      body: quotes.map(q => [
        q.estimateNumber ? `#${q.estimateNumber}` : `#${q.id}`,
        q.title,
        q.status,
        fmtDateStr(q.estimateDate),
        fmt(q.total),
      ]),
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 } },
      headStyles: { fillColor: accentRgb, textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
      columnStyles: {
        0: { cellWidth: 22 },
        2: { cellWidth: 26 },
        3: { cellWidth: 28 },
        4: { cellWidth: 30, halign: "right" },
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Invoices
  if (invoices.length > 0) {
    if (y + 30 > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
    sectionHeader(doc, `Invoices (${invoices.length})`, y, margin, rightX, accentRgb);
    y += 8;
    autoTable(doc, {
      startY: y,
      head: [["#", "Title", "Status", "Date", "Paid", "Total"]],
      body: invoices.map(inv => [
        inv.invoiceNumber ? `#${inv.invoiceNumber}` : `#${inv.id}`,
        inv.title,
        inv.status,
        fmtDateStr(inv.invoiceDate),
        fmt(inv.paid),
        fmt(inv.total),
      ]),
      theme: "plain",
      styles: { fontSize: 9, cellPadding: { top: 3, bottom: 3, left: 4, right: 4 } },
      headStyles: { fillColor: accentRgb, textColor: [255, 255, 255], fontStyle: "bold" },
      margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
      columnStyles: {
        0: { cellWidth: 22 },
        2: { cellWidth: 24 },
        3: { cellWidth: 26 },
        4: { cellWidth: 26, halign: "right" },
        5: { cellWidth: 28, halign: "right" },
      },
    });
    y = (doc as any).lastAutoTable.finalY + 8;
  }

  // Actual costs grouped by category
  if (costs.length > 0) {
    if (y + 30 > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
    sectionHeader(doc, `Actual Costs (${costs.length})`, y, margin, rightX, accentRgb);
    y += 8;

    const groups = new Map<string, ProjectActualCost[]>();
    for (const c of costs) {
      const key = c.category?.trim() || (c.type === "labour" ? "Labor" : c.type) || "Uncategorized";
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(c);
    }

    let grandTotal = 0;
    const sortedKeys = Array.from(groups.keys()).sort();
    for (const key of sortedKeys) {
      const items = groups.get(key)!;
      const subtotal = items.reduce((s, c) => s + (c.amount || 0), 0);
      grandTotal += subtotal;

      const body: Array<Array<string | CellDef>> = [];
      body.push([{
        content: `${key} — ${fmt(subtotal)}`,
        colSpan: 5,
        styles: { fontStyle: "bold", fillColor: [240, 242, 246], textColor: [30, 30, 30] },
      }]);
      for (const c of items) {
        body.push([
          fmtDateStr(c.date),
          c.name,
          c.vendor ?? "",
          c.quantity != null && c.unitCost != null ? `${c.quantity} × ${fmt(c.unitCost)}` : "",
          fmt(c.amount || 0),
        ]);
      }

      autoTable(doc, {
        startY: y,
        head: [["Date", "Item", "Vendor", "Qty × Unit", "Amount"]],
        body,
        theme: "plain",
        styles: { fontSize: 8.5, cellPadding: { top: 2.5, bottom: 2.5, left: 4, right: 4 } },
        headStyles: { fillColor: accentRgb, textColor: [255, 255, 255], fontStyle: "bold", fontSize: 9 },
        margin: { left: margin, right: margin, bottom: FOOTER_RESERVE },
        columnStyles: {
          0: { cellWidth: 24 },
          3: { cellWidth: 32, halign: "right" },
          4: { cellWidth: 28, halign: "right", fontStyle: "bold" },
        },
      });
      y = (doc as any).lastAutoTable.finalY + 4;
    }

    if (y + 10 > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
    doc.setDrawColor(accentRgb[0], accentRgb[1], accentRgb[2]);
    doc.setLineWidth(0.4);
    doc.line(margin, y, rightX, y);
    y += 6;
    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.setTextColor(30, 30, 30);
    doc.text("Grand Total (Actual Costs)", margin, y);
    doc.text(fmt(grandTotal), rightX, y, { align: "right" });
    y += 8;
  }

  // Notes
  if (project.notes?.trim()) {
    if (y + 24 > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
    sectionHeader(doc, "Notes", y, margin, rightX, accentRgb);
    y += 8;
    doc.setFont("helvetica", "normal");
    doc.setFontSize(9.5);
    doc.setTextColor(50, 50, 50);
    const lines: string[] = doc.splitTextToSize(project.notes.trim(), rightX - margin);
    for (const line of lines) {
      if (y > pageHeight - FOOTER_RESERVE) { doc.addPage(); y = margin; }
      doc.text(line, margin, y);
      y += 4.6;
    }
  }

  // Footer
  const totalPages = doc.getNumberOfPages();
  const generatedAt = new Date().toLocaleString("en-US", {
    year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit",
  });
  const footerText = company.pdfFooter?.trim() || "Generated by QuoTrak";
  const footerY = pageHeight - 8;
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7.5);
    doc.setTextColor(150, 150, 150);
    doc.setDrawColor(210, 210, 210);
    doc.setLineWidth(0.3);
    doc.line(margin, footerY - 3.5, rightX, footerY - 3.5);
    doc.text(`${footerText} • Generated ${generatedAt}`, margin, footerY, { align: "left" });
    doc.text(`Page ${i} of ${totalPages}`, rightX, footerY, { align: "right" });
  }

  return doc;
}

function sectionHeader(
  doc: jsPDF,
  title: string,
  y: number,
  margin: number,
  rightX: number,
  accentRgb: [number, number, number],
) {
  doc.setFillColor(accentRgb[0], accentRgb[1], accentRgb[2]);
  doc.rect(margin, y, 3, 5, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(12);
  doc.setTextColor(30, 30, 30);
  doc.text(title, margin + 6, y + 4.2);
  doc.setDrawColor(220, 220, 220);
  doc.setLineWidth(0.3);
  doc.line(margin, y + 6.5, rightX, y + 6.5);
}

function sanitizeFilename(name: string): string {
  return name
    .replace(/[\\/:*?"<>|\x00-\x1f]/g, "_")
    .replace(/^\.+/, "_")
    .replace(/\s+/g, " ")
    .trim()
    .slice(0, 180) || "untitled";
}

function dedupe(used: Set<string>, name: string): string {
  if (!used.has(name)) {
    used.add(name);
    return name;
  }
  const dot = name.lastIndexOf(".");
  const base = dot > 0 ? name.slice(0, dot) : name;
  const ext = dot > 0 ? name.slice(dot) : "";
  let i = 2;
  while (used.has(`${base} (${i})${ext}`)) i++;
  const next = `${base} (${i})${ext}`;
  used.add(next);
  return next;
}

function summaryFilename(project: ProjectDetail): string {
  return sanitizeFilename(`Summary - ${project.name}.pdf`);
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

export function exportProjectSummaryPDF(
  input: ProjectSummaryInput,
  company: CompanySettings = {},
): void {
  const doc = buildProjectSummaryPDFDoc(input, company);
  doc.save(summaryFilename(input.project));
}

export interface ArchiveResult {
  zipName: string;
  failures: string[];
}

export async function exportProjectArchive(
  input: ProjectSummaryInput,
  company: CompanySettings = {},
): Promise<ArchiveResult> {
  const { project, quotes, invoices, files } = input;
  const zip = new JSZip();
  const failures: string[] = [];

  // Summary PDF at root
  try {
    const doc = buildProjectSummaryPDFDoc(input, company);
    zip.file(summaryFilename(project), doc.output("blob"));
  } catch (e) {
    failures.push(`Summary PDF: ${(e as Error).message}`);
  }

  // Quotes
  if (quotes.length > 0) {
    const quotesFolder = zip.folder("Quotes")!;
    const used = new Set<string>();
    for (const q of quotes) {
      try {
        const [estimate, milestones] = await Promise.all([
          getEstimate(q.id),
          listEstimateMilestones(q.id).catch(() => []),
        ]);
        const data: EstimateData = {
          ...(estimate as unknown as EstimateData),
          milestones: milestones as EstimateData["milestones"],
        };
        const doc = buildCustomerPDFDoc(data, company);
        const name = dedupe(used, sanitizeFilename(buildPdfFilename(data)));
        quotesFolder.file(name, doc.output("blob"));
      } catch (e) {
        failures.push(`Quote #${q.estimateNumber ?? q.id}: ${(e as Error).message}`);
      }
    }
  }

  // Invoices
  if (invoices.length > 0) {
    const invFolder = zip.folder("Invoices")!;
    const used = new Set<string>();
    for (const inv of invoices) {
      try {
        const [invoice, milestones, payments] = await Promise.all([
          getInvoice(inv.id),
          listMilestones(inv.id).catch(() => []),
          listInvoicePayments(inv.id).catch(() => []),
        ]);
        const data: InvoiceData = {
          ...(invoice as unknown as InvoiceData),
          createdAt: String((invoice as { createdAt: unknown }).createdAt),
          milestones: milestones as InvoiceData["milestones"],
          payments: payments as InvoiceData["payments"],
        };
        const doc = await buildInvoicePDFDoc(data, company);
        const name = dedupe(used, sanitizeFilename(buildInvoiceFilename(data)));
        invFolder.file(name, doc.output("blob"));
      } catch (e) {
        failures.push(`Invoice #${inv.invoiceNumber ?? inv.id}: ${(e as Error).message}`);
      }
    }
  }

  // Files
  if (files.length > 0) {
    const filesFolder = zip.folder("Files")!;
    const used = new Set<string>();
    for (const f of files) {
      try {
        const res = await fetch(`/api/storage${f.objectPath}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        const name = dedupe(used, sanitizeFilename(f.fileName));
        filesFolder.file(name, blob);
      } catch (e) {
        failures.push(`File "${f.fileName}": ${(e as Error).message}`);
      }
    }
  }

  const zipName = sanitizeFilename(`${project.name} - Project Export.zip`);
  const content = await zip.generateAsync({ type: "blob" });
  downloadBlob(content, zipName);

  return { zipName, failures };
}
